import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { $app } from '../app/App';
import RegistrationForm from '../components/RegistrationForm';
import VerifyEmail from '../components/VerifyEmail';
import {
  CONSENT_KEYS,
  DEFAULT_COUNTRY,
  DEFAULT_PHONE_PREFIX,
  daysInMonth,
  formatPhone,
  formatZip,
  isBirthDateValid,
  isOldEnough,
  isPhoneValid,
  toIsoDate,
  toPhoneE164,
} from '../helpers/registration';
import { getStoreId } from '../helpers/storeAttribution';
import locales from '../locales.json';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const { REGISTRATION_TEXTS, API_TEXTS } = locales;

const EMAIL_STEP = '/verify-email';

const INITIAL_VALUES = {
  salutation: '',
  firstName: '',
  lastName: '',
  phonePrefix: DEFAULT_PHONE_PREFIX,
  phone: '',
  birthDay: '',
  birthMonth: '',
  birthYear: '',
  street: '',
  city: '',
  zip: '',
  country: DEFAULT_COUNTRY,
};

const INITIAL_CONSENTS = Object.fromEntries(CONSENT_KEYS.map((key) => [key, false]));

// Poradi rozhoduje, na kterou chybu stranka odscrolluje jako prvni.
const VALIDATORS = [
  ['salutation', (v) => !v.salutation, REGISTRATION_TEXTS.errorSalutation],
  ['firstName', (v) => !v.firstName.trim(), REGISTRATION_TEXTS.errorFirstName],
  ['lastName', (v) => !v.lastName.trim(), REGISTRATION_TEXTS.errorLastName],
  ['phone', (v) => !isPhoneValid(v.phonePrefix, v.phone), REGISTRATION_TEXTS.errorPhone],
  [
    'birthDate',
    (v) => !isBirthDateValid(v.birthYear, v.birthMonth, v.birthDay),
    REGISTRATION_TEXTS.errorBirthDate,
  ],
  [
    'birthDate',
    (v) =>
      isBirthDateValid(v.birthYear, v.birthMonth, v.birthDay) &&
      !isOldEnough(v.birthYear, v.birthMonth, v.birthDay),
    REGISTRATION_TEXTS.errorBirthDateMinAge,
  ],
  ['street', (v) => !v.street.trim(), REGISTRATION_TEXTS.errorStreet],
  ['city', (v) => !v.city.trim(), REGISTRATION_TEXTS.errorCity],
  ['zip', (v) => !formatZip(v.zip), REGISTRATION_TEXTS.errorZip],
];

// Datum narozeni je jedna hlaska pro tri vybery.
const ERROR_OF_FIELD = {
  birthDay: 'birthDate',
  birthMonth: 'birthDate',
  birthYear: 'birthDate',
};

const validate = (values) =>
  VALIDATORS.reduce((errors, [name, isInvalid, message]) => {
    if (isInvalid(values)) errors[name] = message;
    return errors;
  }, {});

// Prvni chybova hlaska pro datum narozeni, nebo prazdny retezec.
const birthDateError = (values) =>
  VALIDATORS.filter(([name]) => name === 'birthDate')
    .find(([, isInvalid]) => isInvalid(values))?.[2] ?? '';

// Formular posila plochy seznam poli, do tvaru CareCloudu
// (customer.personal_information) je prevadi az registracni endpoint.
const buildPayload = (email, values, consents) => ({
  customer: {
    salutation: values.salutation || null,
    first_name: values.firstName.trim(),
    last_name: values.lastName.trim(),
    email,
    phone: toPhoneE164(values.phonePrefix, values.phone),
    birthdate: toIsoDate(values.birthYear, values.birthMonth, values.birthDay),
    street: values.street.trim(),
    city: values.city.trim(),
    zip: values.zip.replace(/\s/g, ''),
    store_id: getStoreId(),
    country: values.country,
    agreements: {
      personal_data: consents.personalData,
      vip: consents.vip,
    },
  },
});

const RegistrationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email ?? '';
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [values, setValues] = useState(INITIAL_VALUES);
  const [consents, setConsents] = useState(INITIAL_CONSENTS);
  const [errors, setErrors] = useState({});
  const [consentError, setConsentError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifySent, setVerifySent] = useState(false);

  const fields = useRef({});
  const refCallbacks = useRef({});
  const abortRef = useRef(null);

  // Bez overeneho e-mailu nema formular co odeslat - zpatky na krok 1.
  useEffect(() => {
    if (!email) navigate(EMAIL_STEP, { replace: true });
  }, [email, navigate]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const fieldRef = useCallback((name) => {
    if (!refCallbacks.current[name]) {
      refCallbacks.current[name] = (node) => {
        fields.current[name] = node;
      };
    }

    return refCallbacks.current[name];
  }, []);

  const handleChange = (name, value) => {
    const next = { ...values, [name]: value };

    if (name === 'phone' || name === 'phonePrefix') {
      next.phone = formatPhone(next.phonePrefix, next.phone);
    }

    if (name === 'zip') {
      next.zip = formatZip(next.zip);
    }

    // Zmena mesice nebo roku muze zkratit mesic - 31. unor by jinak zustal vybrany.
    if (name === 'birthMonth' || name === 'birthYear') {
      if (Number(next.birthDay) > daysInMonth(next.birthYear, next.birthMonth)) {
        next.birthDay = '';
      }
    }

    setValues(next);

    const errorName = ERROR_OF_FIELD[name] || name;

    // Jakmile jsou vyplneny vsechny tri vybery data narozeni, kontrolujeme hned.
    if (errorName === 'birthDate' && next.birthDay && next.birthMonth && next.birthYear) {
      setErrors((previous) => ({ ...previous, birthDate: birthDateError(next) }));
    } else {
      setErrors((previous) => (previous[errorName] ? { ...previous, [errorName]: '' } : previous));
    }

    setSubmitError('');
  };

  const handleConsentChange = (key, checked) => {
    setConsents((previous) => ({ ...previous, [key]: checked }));
    setSubmitError('');
  };

  const handleChangeEmail = () => navigate(EMAIL_STEP, { state: { email } });

  const closeVerify = () => navigate('/', { replace: true });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    const nextErrors = validate(values);
    const missingConsent = Object.values(consents).some((granted) => !granted);

    setErrors(nextErrors);
    setConsentError(missingConsent);

    const firstInvalid =
      VALIDATORS.map(([name]) => name).find((name) => nextErrors[name]) ||
      (missingConsent ? 'consents' : null);

    if (firstInvalid) {
      fields.current[firstInvalid]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setSubmitError('');

    try {
      if (!executeRecaptcha) {
        setSubmitError(API_TEXTS.recaptcha_failed);
        return;
      }
      const recaptcha_token = await executeRecaptcha('bandi_register');
      await $app.register(buildPayload(email, values, consents), {
        signal: controller.signal,
      }, recaptcha_token);

      setVerifySent(true);
    } catch (error) {
      if (error.exception !== 'canceled') {
        setSubmitError(error.message ?? API_TEXTS.clientError);
      }
    } finally {
      abortRef.current = null;
      setLoading(false);
    }
  };

  return (
    <>
      <RegistrationForm
        email={email}
        values={values}
        consents={consents}
        errors={errors}
        consentError={consentError}
        loading={loading}
        submitError={submitError}
        fieldRef={fieldRef}
        onChange={handleChange}
        onConsentChange={handleConsentChange}
        onChangeEmail={handleChangeEmail}
        onSubmit={handleSubmit}
        onBack={handleChangeEmail}
      />

      <AnimatePresence>
        {verifySent && <VerifyEmail email={email} onClose={closeVerify} />}
      </AnimatePresence>
    </>
  );
};

export default RegistrationPage;
